import AllergyService from "../allergies/AllergyService";

/**
 * Shared per-session cache for a patient's active SALT-category allergies.
 * Consumed by:
 *
 *   • Medicines.js — feeds the top-of-section "Salt allergies" strip
 *   • MedicineForm.js — feeds the Add-medicine catalog-pick guard
 *   • MedicineCatalogAutocomplete — grays out matching catalog rows
 *
 * The list is cached by patientId; a second caller within the same session
 * gets a resolved promise instantly. Concurrent first-callers coalesce on
 * the in-flight promise so only one AllergyService.list request goes out.
 *
 * Prior version fetched salt data from the medicine catalog to enrich
 * drug-category allergies. Not needed now — allergies ARE salt names, so
 * the record's `.name` field is the salt to compare against.
 *
 * `invalidateSaltAllergies(patientId)` drops the cache — call after any
 * mutation on the Allergies section if you want the strip / guard to
 * reflect the change on the next Medicines render. (Not wired yet; the
 * two sections don't cross-talk today.)
 */

const cache = new Map();    // patientId -> Allergy[]
const inflight = new Map(); // patientId -> Promise<Allergy[]>

const saltsFromList = (res) =>
  ((res && res.allergies) || []).filter(
    (a) => (a.category || "") === "Salt"
  );

export const fetchSaltAllergies = (patientId) => {
  if (!patientId) return Promise.resolve([]);
  if (cache.has(patientId)) return Promise.resolve(cache.get(patientId));
  if (inflight.has(patientId)) return inflight.get(patientId);

  const p = new Promise((resolve) => {
    const svc = new AllergyService();
    svc.list(
      patientId,
      (res) => {
        const salts = saltsFromList(res);
        cache.set(patientId, salts);
        inflight.delete(patientId);
        resolve(salts);
      },
      () => {
        // Non-fatal — an empty list keeps prescriptions unblocked rather
        // than pinning them on an allergy service outage.
        cache.set(patientId, []);
        inflight.delete(patientId);
        resolve([]);
      }
    );
  });
  inflight.set(patientId, p);
  return p;
};

export const invalidateSaltAllergies = (patientId) => {
  if (patientId == null) {
    cache.clear();
    inflight.clear();
    return;
  }
  cache.delete(patientId);
  inflight.delete(patientId);
};

/* ─── Match rule (used by both the guard and the dropdown gray-out) ─── */

const norm = (s) => String(s || "").trim().toLowerCase();

// Pull the flat list of saltNames from a medicine catalog row. Prefers
// the flat `saltNames` field (added on api-medicine-service's list
// shape) and falls back to walking the nested `saltTherapy` tree for
// defensiveness. De-duped + lowercased for cheap set-intersection.
export const extractSaltNamesFromCatalogRow = (row) => {
  if (!row) return [];
  const out = new Set();
  const flat = Array.isArray(row.saltNames) ? row.saltNames : [];
  for (const raw of flat) {
    const name = norm(raw);
    if (name) out.add(name);
  }
  if (out.size > 0) return Array.from(out);
  const therapy = row.saltTherapy || [];
  for (const t of therapy) {
    const salts = (t && t.salt) || [];
    for (const s of salts) {
      const name = norm(s && s.saltName);
      if (name) out.add(name);
    }
  }
  return Array.from(out);
};

/**
 * Match a picked medicine's salt set against the patient's Salt-category
 * allergies. Every allergy carries a salt name in `.name` (that's what
 * the allergy add-flow captures now). Set-intersection: any overlap →
 * match.
 *
 * Return shape:
 *   null                                              // no match
 *   { allergy, matchType: "salt", matchDetail: "…" } // shared salt
 *
 * matchType is kept as `"salt"` (rather than dropped) so the dialog's
 * salt-specific wording ("matches via a shared salt (X)") still fires
 * without any change to the render logic.
 */
export const findSaltAllergyMatch = (medSaltNames, saltAllergies) => {
  if (!saltAllergies || saltAllergies.length === 0) return null;
  const medSet = new Set((medSaltNames || []).map(norm).filter(Boolean));
  if (medSet.size === 0) return null;
  for (const a of saltAllergies) {
    const nA = norm(a.name);
    if (!nA) continue;
    if (medSet.has(nA)) {
      return { allergy: a, matchType: "salt", matchDetail: nA };
    }
  }
  return null;
};

// Present the salt name in a display-friendly form. We store lower-case
// for comparison; capitalise the first letter for the dialog.
export const prettySalt = (s) => {
  if (!s) return "";
  const t = String(s).trim();
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
};
